import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { apiAxios as axios } from "../../utils/apiClient.js";
import { useAuth } from "../../utils/AuthContext";
import { url } from "../../Constant/TextConstant";
import "./MyOrderDetails.css";
import { Helmet } from "react-helmet";
import Header from "../Header";
import { decryptData } from "../../utils/encryption";
import {
  fetchUniqueIds,
  sendFCMNotification,
} from "../../Services/NotificationServices";
import {
  FaBox,
  FaShippingFast,
  FaCheckCircle,
  FaTimesCircle,
  FaClock,
  FaMoneyBillWave,
  FaMapMarkerAlt,
  FaCreditCard,
  FaBarcode,
  FaTruck,
  FaShoppingBag,
  FaTrash,
} from "react-icons/fa";
import ReviewScreenForProductAfterDelivered from "../../components/ReviewScreenAfterDelivered/ReviewScreenAfterDelivered";
import Footer from "../Footer/Footer";
import { useUserLocation } from "../../utils/useUserLocation";

const MyOrderDetailScreen = () => {
  const { encryptOrderNumber } = useParams();
  const orderNumber = decryptData(encryptOrderNumber);
  const [orderDetails, setOrderDetails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { currentUser , currentUserName} = useAuth();

  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [cancellingItem, setCancellingItem] = useState(null); // null for whole order, or item ID
  const [isCancelling, setIsCancelling] = useState(false);
  const [cancelSuccess, setCancelSuccess] = useState(false);
  const [cancelError, setCancelError] = useState("");
  const [completedItems, setCompletedItems] = useState(0);
  // Replace the existing location state with the useUserLocation hook
  const {
    location: currentLocation,
    error: locationError,
    isLoading: isLocationLoading,
    updateLocation,
  } = useUserLocation();

  const [showModal, setShowModal] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(() => {
    return localStorage.getItem("selectedCategory") || "products";
  });

   useEffect(() => {
      window.scrollTo(0, 0);
    }, []);
  // Update localStorage whenever selectedCategory changes
  useEffect(() => {
    localStorage.setItem("selectedCategory", selectedCategory);
  }, [selectedCategory]);

  // Handler for modal close with location update
  const handleModalClose = (shouldClose, newCoordinates = null) => {
    setShowModal(false);
    if (newCoordinates) {
      // Update location through the hook's update function
      updateLocation({
        coordinates: {
          latitude: newCoordinates.latitude,
          longitude: newCoordinates.longitude,
        },
        address: newCoordinates.address || "",
        details: newCoordinates.details || null,
      });
    }
  };
  const fetchOrderDetails = async () => {
    try {
      const response = await axios.get(
        `${url.baseAPIUrl}/api/orderdetails/getorderdetails?CustomerMobile=${currentUser}&orderNumber=${orderNumber}`,
        { headers: { Accept: "application/json" } }
      );

      if (response.status === 200) {
        setOrderDetails(response.data);
      } else {
        throw new Error("Failed to load order details");
      }
    } catch (err) {
      setError("Error fetching order details.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrderDetails();
  }, [orderNumber, currentUser]);

  const getStatusColor = (status) => {
    const statusLower = (status || "").toLowerCase();
    if (statusLower.includes("delivered")) return "#4CAF50";
    if (statusLower.includes("pending")) return "#FF9800";
    if (statusLower.includes("processing")) return "#2196F3";
    if (statusLower.includes("cancelled")) return "#F44336";
    if (statusLower.includes("shipped")) return "#673AB7";
    return "#607D8B";
  };

  const getStatusIcon = (status) => {
    const normalizedStatus = normalizeStatus(status);

    switch (normalizedStatus) {
      case "Pending":
        return <FaClock />;
      case "Accepted":
        return <FaBox />;
      case "Pickup":
        return <FaTruck />;
      case "Shipped":
        return <FaShippingFast />;
      case "Delivered":
        return <FaCheckCircle />;
      case "Cancelled":
        return <FaTimesCircle />;
      default:
        return <FaBox />;
    }
  };

  function normalizeStatus(status) {
    if (!status) return "pending";

    const statusLower = status.toLowerCase();
    if (statusLower.includes("processing") || statusLower.includes("pending")) {
      return "Pending";
    }
    if (statusLower.includes("accept")) {
      return "Accepted";
    }
    if (statusLower.includes("pickup") || statusLower.includes("pick-up")) {
      return "Pickup";
    }
    if (statusLower.includes("shipped")) {
      return "Shipped";
    }
    if (statusLower.includes("delivered")) {
      return "Delivered";
    }
    if (
      statusLower.includes("cancelled") ||
      statusLower.includes("cancel") ||
      statusLower.includes("reject")
    ) {
      return "Cancelled";
    }
    return statusLower;
  }

  // Generate structured data for the order
  const generateOrderStructuredData = () => {
    if (!orderDetails || orderDetails.length === 0) return null;

    const mainOrder = orderDetails[0];
    const orderDate = new Date(mainOrder.Date || mainOrder.Time);
    const orderItems = orderDetails.map((item) => ({
      "@type": "Product",
      name: item.ProductName,
      sku: item.ProductCode,
      image: item.Image ? `${url.productImageStartingPath}/${item.Image}` : "",
      price: item.Price,
      priceCurrency: "INR",
      quantity: item.Qty,
    }));

    return {
      "@context": "https://schema.org",
      "@type": "Order",
      orderNumber: orderNumber,
      orderDate: orderDate.toISOString(),
      orderStatus: normalizeStatus(mainOrder.DeliveryStatus),
      price: mainOrder.Total,
      priceCurrency: "INR",
      acceptedOffer: orderItems,
      seller: {
        "@type": "Organization",
        name: mainOrder.StoreName || "MyStore",
      },
      customer: {
        "@type": "Person",
        name: mainOrder.Name || "Customer",
      },
      deliveryAddress: {
        "@type": "PostalAddress",
        streetAddress: mainOrder.FullAddress,
      },
    };
  };

  const getStatusSteps = (status) => {
    const normalizedStatus = normalizeStatus(status);
    const steps = [
      { id: 1, name: "Order Placed", completed: true, active: false },
      { id: 2, name: "Processing", completed: false, active: false },
      { id: 3, name: "Shipped", completed: false, active: false },
      { id: 4, name: "Delivered", completed: false, active: false },
    ];

    switch (normalizedStatus) {
      case "Pending":
      case "Accepted":
        steps[1].completed = true;
        steps[1].active = true;
        break;
      case "Pickup":
        steps[1].completed = true;
        steps[2].completed = true;
        steps[2].active = true;
        break;
      case "Shipped":
        steps[1].completed = true;
        steps[2].completed = true;
        steps[2].active = true;
        break;
      case "Delivered":
        steps.forEach((step) => {
          step.completed = true;
          if (step.id === 4) step.active = true;
        });
        break;
      case "Cancelled":
        steps.forEach((step) => {
          step.completed = false;
        });
        break;
      default:
        break;
    }

    return steps;
  };

  const handleCancelOrder = (item = null) => {
    setCancellingItem(item);
    setCancelReason("");
    setShowCancelDialog(true);
    setCancelSuccess(false);
    setCancelError("");
  };

  const confirmCancel = async () => {
    if (!cancelReason.trim()) {
      setCancelError("Please provide a cancellation reason");
      return;
    }

    setIsCancelling(true);
    setCancelError("");
    setCompletedItems(0); // Reset progress counter

    try {
      if (cancellingItem) {
        // Single item cancellation
        const payload = {
          OrderNumber: orderNumber,
          StoreMobile: orderDetails[0].StoreMobile,
          Status: "Cancelled",
          CancelReason: cancelReason,
          Cashback: 0,
          ID: cancellingItem.ID,
        };

        const response = await axios.post(
          `${url.baseAPIUrl}/api/orderdetails/updateorderdetails`,
          payload,
          { headers: { Accept: "application/json" } }
        );

        if (response.status === 200) {
          setCancelSuccess(true);
          setTimeout(() => {
            fetchOrderDetails();
            setShowCancelDialog(false);
          }, 1500);

           const uniqueIds = await fetchUniqueIds(orderDetails[0].StoreMobile);
           if (uniqueIds) {
             await sendFCMNotification({
               Tokens: uniqueIds,
               Title: "Order Cancelled",
               Body: `Product ${cancellingItem.ProductName} has been Cancelled by ${
                 currentUserName || "Customer"
               }`,
               ScreenRoute: "/orderDetails",
             });
           }
        } else {
          throw new Error("Failed to cancel item");
        }
      } else {
        // Entire order cancellation - cancel each item individually
        let allSuccessful = true;

        for (const item of orderDetails) {
          const payload = {
            OrderNumber: orderNumber,
            StoreMobile: orderDetails[0].StoreMobile,
            Status: "Cancelled",
            CancelReason: cancelReason,
            Cashback: 0,
            ID: item.ID,
          };

          const response = await axios.post(
            `${url.baseAPIUrl}/api/orderdetails/updateorderdetails`,
            payload,
            { headers: { Accept: "application/json" } }
          );

          if (response.status !== 200) {
            allSuccessful = false;
            break;
          }

          setCompletedItems((prev) => prev + 1); // Update progress
        }

        if (allSuccessful) {
          setCancelSuccess(true);
          setTimeout(() => {
            fetchOrderDetails();
            setShowCancelDialog(false);
          }, 1500);

          
           const uniqueIds = await fetchUniqueIds(orderDetails[0].StoreMobile);
           if (uniqueIds) {
             await sendFCMNotification({
               Tokens: uniqueIds,
               Title: "Order Cancelled",
               Body: `Order Number ${
                 orderNumber
               } has been Cancelled by ${currentUserName || "Customer"}`,
               ScreenRoute: "/orderDetails",
             });
           }
        } else {
          throw new Error("Failed to cancel some items");
        }
      }
    } catch (err) {
      setCancelError(
        err.response?.data?.message || "Failed to cancel. Please try again."
      );
    } finally {
      setIsCancelling(false);
    }
  };

  const canCancelOrder = () => {
    if (!orderDetails || orderDetails.length === 0) return false;
    const status = orderDetails[0].DeliveryStatus || "";
    return (
      !normalizeStatus(status).includes("Cancelled") &&
      !normalizeStatus(status).includes("Delivered") &&
      !normalizeStatus(status).includes("Shipped")
    );
  };

  const canCancelItem = (item) => {
    const status = item.DeliveryStatus || orderDetails[0].DeliveryStatus || "";
    return (
      !normalizeStatus(status).includes("Cancelled") &&
      !normalizeStatus(status).includes("Delivered") &&
      !normalizeStatus(status).includes("Shipped")
    );
  };

  if (loading) {
    return (
      <>
        {" "}
        <Helmet>
          <title>Loading Order Details | MyStore</title>
          <meta name="description" content="Loading your order details" />
        </Helmet>
        <div className="order-loading">
          <div className="order-spinner"></div>
          <span className="order-loading-text">Loading order details...</span>
        </div>
      </>
    );
  }

  if (error) {
    return (
      <>
        {" "}
        <Helmet>
          <title>Error Loading Order | MyStore</title>
          <meta
            name="description"
            content="Error occurred while loading your order details"
          />
        </Helmet>
        <div className="order-error-container">
          <div className="order-error-alert">
            <span className="order-error-icon">⚠️</span>
            <div>
              <h3 className="order-error-title">Error loading order</h3>
              <p className="order-error-message">{error}</p>
            </div>
          </div>
          <button
            className="order-error-button"
            onClick={() => window.location.reload()}
          >
            🔄 Try Again
          </button>
        </div>
      </>
    );
  }

  if (orderDetails.length === 0) {
    return (
      <>
        {" "}
        <Helmet>
          <title>Order Not Found | MyStore</title>
          <meta
            name="description"
            content="We couldn't find details for this order"
          />
        </Helmet>
        <div className="order-empty-container">
          <h2 className="order-empty-title">No Order Details Found</h2>
          <p className="order-empty-text">
            We couldn't find any details for this order.
          </p>
        </div>
      </>
    );
  }

  const statusSteps = getStatusSteps(orderDetails[0].DeliveryStatus);
  const orderDate = new Date(orderDetails[0].Date || orderDetails[0].Time);
  const formattedDate = orderDate.toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
  const structuredData = generateOrderStructuredData();
  return (
    <>
      <Helmet>
        <title>{`Order #${orderNumber} Details | MyStore`}</title>
        <meta
          name="description"
          content={`View details for order #${orderNumber} - Status: ${orderDetails[0].DeliveryStatus}, Total: ₹${orderDetails[0].Total}`}
        />
        <meta
          name="keywords"
          content={`order details, order #${orderNumber}, tracking, MyStore order`}
        />
        <meta
          property="og:title"
          content={`Order #${orderNumber} Details | MyStore`}
        />
        <meta
          property="og:description"
          content={`View details and track your order #${orderNumber} on MyStore`}
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={window.location.href} />
        <meta
          property="og:image"
          content={`${window.location.origin}/order-details-og-image.jpg`}
        />
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content={`Order #${orderNumber} Details | MyStore`}
        />
        <meta
          name="twitter:description"
          content={`Track your order #${orderNumber} on MyStore`}
        />
        <meta
          name="twitter:image"
          content={`${window.location.origin}/order-details-twitter-image.jpg`}
        />
        <link rel="canonical" href={window.location.href} />
        {structuredData && (
          <script type="application/ld+json">
            {JSON.stringify(structuredData)}
          </script>
        )}
      </Helmet>
      <Header
        userLocation={currentLocation} // Use currentLocation from hook
        setShowModal={setShowModal}
        setUserLocation={updateLocation} // Use updateLocation from hook
        showModal={showModal}
        selectedCategory={selectedCategory}
        setSelectedCategory={setSelectedCategory}
      />
      <div className="my-order-detail-container">
        <div className="my-order-detail-header">
          <h1 className="my-order-detail-title">
            <FaShoppingBag /> Order #{orderNumber}
          </h1>
          <div className="my-order-detail-meta">
            <span className="my-order-detail-date">{formattedDate}</span>
            <span className="my-order-detail-total">
              ₹{orderDetails[0].Total}
            </span>
          </div>
        </div>

        <div className="my-order-detail-status-container">
          <div
            className="my-order-detail-status-badge"
            style={{
              backgroundColor: getStatusColor(orderDetails[0].DeliveryStatus),
              boxShadow: `0 4px 12px ${getStatusColor(
                orderDetails[0].DeliveryStatus
              )}40`,
            }}
          >
            <span className="status-icon">
              {getStatusIcon(orderDetails[0].DeliveryStatus)}
            </span>
            {orderDetails[0].DeliveryStatus}
          </div>
          {canCancelOrder() && (
            <button
              className="cancel-order-button"
              onClick={() => handleCancelOrder()}
            >
              <FaTimesCircle /> Cancel Entire Order
            </button>
          )}
        </div>

        <div className="my-order-detail-content">
          <div className="my-order-detail-timeline">
            <div className="my-order-detail-timeline-header">
              <h3>Order Progress</h3>
              <div className="my-order-detail-timeline-line"></div>
            </div>
            <div className="my-order-detail-timeline-steps">
              {statusSteps.map((step) => (
                <div
                  key={step.id}
                  className={`my-order-detail-timeline-step ${
                    step.completed ? "completed" : ""
                  } ${step.active ? "active" : ""}`}
                >
                  <div className="my-order-detail-step-marker">
                    <div className="my-order-detail-step-icon">
                      {step.completed ? (
                        <FaCheckCircle className="completed-icon" />
                      ) : (
                        getStatusIcon(step.name)
                      )}
                    </div>
                    {step.id < statusSteps.length && (
                      <div className="my-order-detail-step-connector"></div>
                    )}
                  </div>
                  <div className="my-order-detail-step-details">
                    <div className="my-order-detail-step-name">{step.name}</div>
                    {step.active && (
                      <div className="my-order-detail-step-status">
                        Current Status
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="my-order-detail-details">
            <div className="my-order-detail-section my-order-detail-products-section">
              <h3 className="my-order-detail-section-title">
                Products Ordered
              </h3>
              <div className="my-order-detail-products-list">
                {orderDetails.map((product, index) => (
                  <div key={index} className="my-order-detail-product-card">
                    <div className="my-order-detail-product-image">
                      <img
                        src={
                          product.Image
                            ? `${url.productImageStartingPath}/${product.Image}`
                            : "https://via.placeholder.com/150?text=Product"
                        }
                        alt={product.ProductName}
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src =
                            "https://via.placeholder.com/150?text=Product";
                        }}
                      />
                    </div>
                    <div className="my-order-detail-product-info">
                      <h4 className="my-order-detail-product-name">
                        {product.ProductName}
                      </h4>
                      <div className="my-order-detail-product-meta">
                        <span className="my-order-detail-product-price">
                          <FaMoneyBillWave /> ₹{product.Price}
                        </span>
                        <span className="my-order-detail-product-qty">
                          Qty: {product.Qty}
                        </span>
                        {product.Size && (
                          <span className="my-order-detail-product-size">
                            Size: {product.Size}
                          </span>
                        )}
                      </div>
                      <div className="my-order-detail-product-code">
                        <FaBarcode /> {product.ProductCode}
                      </div>
                      {canCancelItem(product) && (
                        <button
                          className="cancel-item-button"
                          onClick={() => handleCancelOrder(product)}
                        >
                          <FaTrash /> Cancel Item
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="my-order-detail-info-grid">
              <div className="my-order-detail-section my-order-detail-shipping-section">
                <h3 className="my-order-detail-section-title">
                  <FaTruck /> Shipping Information
                </h3>
                <div className="my-order-detail-shipping-address">
                  <p>{orderDetails[0].FullAddress}</p>
                </div>
                <div className="my-order-detail-shipping-meta">
                  <div className="my-order-detail-meta-item">
                    <span className="my-order-detail-meta-label">
                      <FaMapMarkerAlt /> Delivery PIN:
                    </span>
                    <span>{orderDetails[0].DeliveryPin || "N/A"}</span>
                  </div>
                  <div className="my-order-detail-meta-item">
                    <span className="my-order-detail-meta-label">
                      <FaShippingFast /> Shipment Number:
                    </span>
                    <span>{orderDetails[0].ShipmentNumber || "N/A"}</span>
                  </div>
                </div>
              </div>

              <div className="my-order-detail-section my-order-detail-payment-section">
                <h3 className="my-order-detail-section-title">
                  <FaCreditCard /> Payment Information
                </h3>
                <div className="my-order-detail-payment-meta">
                  <div className="my-order-detail-meta-item">
                    <span className="my-order-detail-meta-label">
                      Payment Status:
                    </span>
                    <span
                      className="my-order-detail-payment-status"
                      style={{
                        color:
                          orderDetails[0].Payment_Status === "Paid"
                            ? "#4CAF50"
                            : "#F44336",
                      }}
                    >
                      {orderDetails[0].Payment_Status}
                    </span>
                  </div>
                  <div className="my-order-detail-meta-item">
                    <span className="my-order-detail-meta-label">
                      Shipping Fee:
                    </span>
                    <span>₹{orderDetails[0].Shipping || "0"}</span>
                  </div>
                  {orderDetails[0].CashbackApply && (
                    <div className="my-order-detail-meta-item my-order-detail-cashback">
                      <span className="my-order-detail-meta-label">
                        Cashback:
                      </span>
                      <span className="my-order-detail-cashback-amount">
                        ₹{orderDetails[0].cashback || "0"}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
            {orderDetails[0].DeliveryStatus === "Delivered" && (
              <ReviewScreenForProductAfterDelivered
                providerMobile={orderDetails[0].StoreMobile}
                productCode={orderDetails[0].ProductCode}
              />
            )}
          </div>
        </div>
      </div>

      {/* Cancel Order Dialog */}
      {showCancelDialog && (
        <div className="cancel-dialog-overlay">
          <div className="cancel-dialog">
            <h3>
              {cancellingItem
                ? "Cancel Item"
                : "Cancel Entire Order (All Items)"}
            </h3>
            {cancellingItem ? (
              <p>
                You're about to cancel:{" "}
                <strong>{cancellingItem.ProductName}</strong>
              </p>
            ) : (
              <p>
                This will cancel all {orderDetails.length} items in your order.
              </p>
            )}

            {!cancellingItem && isCancelling && (
              <div className="cancellation-progress">
                Cancelling item {completedItems + 1} of {orderDetails.length}...
                <progress value={completedItems} max={orderDetails.length} />
              </div>
            )}

            <p>Please provide a reason for cancellation:</p>
            <textarea
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              placeholder="Enter cancellation reason..."
              rows={3}
              disabled={isCancelling}
            />

            {cancelError && <div className="cancel-error">{cancelError}</div>}
            {cancelSuccess && (
              <div className="cancel-success">
                {cancellingItem
                  ? "Item cancelled successfully!"
                  : "Order cancelled successfully!"}
              </div>
            )}

            <div className="cancel-dialog-buttons">
              <button
                onClick={() => setShowCancelDialog(false)}
                disabled={isCancelling}
              >
                Go Back
              </button>
              <button
                onClick={confirmCancel}
                disabled={isCancelling || cancelSuccess}
                className={isCancelling ? "loading" : ""}
              >
                {isCancelling
                  ? cancellingItem
                    ? "Cancelling..."
                    : "Cancelling Items..."
                  : cancelSuccess
                  ? "Done"
                  : "Confirm Cancel"}
              </button>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </>
  );
};

export default MyOrderDetailScreen;
